import 'bootstrap';
import AOS from 'aos';

window.$ = window.jQuery = require('jquery');

AOS.init();

/**
 * Single-page nav: smooth-scroll to in-page sections, keep the current
 * section's nav link highlighted, and compensate for a fixed/sticky header
 * via the --nav-height CSS variable (see app.scss, section[id]).
 */
(function () {
    const header = document.getElementById('site-header');
    const prefersReducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    function updateNavHeightVar() {
        if (!header) return;
        const isFixed = ['fixed', 'sticky'].includes(getComputedStyle(header).position);
        document.documentElement.style.setProperty('--nav-height', isFixed ? `${header.offsetHeight}px` : '0px');
    }
    updateNavHeightVar();
    window.addEventListener('resize', updateNavHeightVar);

    // Solid header background once the page has scrolled past the point
    // where a transparent header would sit over light content.
    const SCROLL_THRESHOLD = 40;
    let ticking = false;

    function updateScrolledState() {
        ticking = false;
        if (!header) return;
        header.classList.toggle('is-scrolled', window.scrollY > SCROLL_THRESHOLD);
    }

    if (header) {
        updateScrolledState();
        window.addEventListener('scroll', () => {
            if (ticking) return;
            ticking = true;
            window.requestAnimationFrame(updateScrolledState);
        }, { passive: true });
    }

    // Mobile hamburger menu: toggles the bars/cross icon and slides the
    // full-width nav panel open, matching the Figma mobile design.
    const navToggle = header ? header.querySelector('.site-header__toggle') : null;
    const mobileNav = document.getElementById('site-header-mobile-nav');

    function closeMobileNav() {
        if (!navToggle || !mobileNav) return;
        navToggle.classList.remove('is-open');
        navToggle.setAttribute('aria-expanded', 'false');
        mobileNav.classList.remove('is-open');
        mobileNav.style.maxHeight = '';
        document.body.classList.remove('nav-open');
    }

    function openMobileNav() {
        if (!navToggle || !mobileNav) return;
        navToggle.classList.add('is-open');
        navToggle.setAttribute('aria-expanded', 'true');
        mobileNav.classList.add('is-open');
        mobileNav.style.maxHeight = `${mobileNav.scrollHeight}px`;
        document.body.classList.add('nav-open');
    }

    if (navToggle && mobileNav) {
        navToggle.addEventListener('click', () => {
            if (navToggle.classList.contains('is-open')) {
                closeMobileNav();
            } else {
                openMobileNav();
            }
        });

        mobileNav.querySelectorAll('a').forEach((link) => {
            link.addEventListener('click', closeMobileNav);
        });

        document.addEventListener('keydown', (event) => {
            if (event.key === 'Escape') closeMobileNav();
        });

        window.addEventListener('resize', () => {
            if (window.innerWidth >= 992) closeMobileNav();
        });
    }

    function scrollToId(id, behavior) {
        const target = id ? document.getElementById(id) : null;
        if (!target) return false;

        const offset = parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--nav-height')) || 0;
        const top = target.getBoundingClientRect().top + window.pageYOffset - offset;
        window.scrollTo({ top, behavior });
        return true;
    }

    const navLinks = Array.from(document.querySelectorAll('header nav a[href^="#"]'));

    navLinks.forEach((link) => {
        link.addEventListener('click', (event) => {
            const id = link.getAttribute('href').slice(1);
            if (!scrollToId(id, prefersReducedMotion() ? 'auto' : 'smooth')) return; // no matching section — let the browser handle it

            event.preventDefault();
            history.pushState(null, '', `#${id}`);
        });
    });

    // A fresh load that already has a #hash (deep link, bookmark, browser
    // back/forward) can get knocked off-target by images/webfonts loading in
    // late and shifting the layout after the browser's own initial jump.
    // Re-settle once everything has finished loading.
    if (location.hash) {
        window.addEventListener('load', () => {
            scrollToId(location.hash.slice(1), 'auto');
        });
    }

    if (!navLinks.length) return;

    // Highlight the nav link for whichever section is currently in view.
    const sections = navLinks
        .map((link) => document.getElementById(link.getAttribute('href').slice(1)))
        .filter(Boolean);

    if (sections.length && 'IntersectionObserver' in window) {
        const setActive = (id) => {
            navLinks.forEach((link) => {
                link.classList.toggle('is-active', link.getAttribute('href') === `#${id}`);
            });
        };

        const observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting) setActive(entry.target.id);
                });
            },
            { rootMargin: '-40% 0px -55% 0px', threshold: 0 }
        );

        sections.forEach((section) => observer.observe(section));
    }
})();
